import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LayoutDashboard, Inbox, CalendarDays, Users, Menu, ArrowLeft, RefreshCw, WifiOff } from "lucide-react";
import { API, apiCall } from "@/components/admin/calendar/calendarUtils";
import { AppointmentDialog } from "@/components/admin/calendar/AppointmentDialog";
import KalenderTab from "@/components/admin/KalenderTab";
import KundenTab from "@/components/admin/KundenTab";
import RechnungenTab from "@/components/admin/RechnungenTab";
import VerfuegbarkeitenTab from "@/components/admin/VerfuegbarkeitenTab";
import { DashboardView, RequestsView } from "@/components/mobile/DashboardView";
import { MoreView } from "@/components/mobile/MoreView";
import { registerSW } from "@/components/mobile/push";

const NAV = [["dashboard", "Start", LayoutDashboard], ["requests", "Anfragen", Inbox], ["calendar", "Kalender", CalendarDays], ["customers", "Kunden", Users], ["more", "Mehr", Menu]];
const TITLES = { dashboard: "Dashboard", requests: "Anfragen", calendar: "Kalender", customers: "Kunden", more: "Mehr", invoices: "Rechnungen", rules: "Buchungsregeln" };
const POLL_MS = 30000;

function Login({ onLogin }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pw }) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Anmeldung fehlgeschlagen");
      onLogin(d.token);
    } catch (e) { toast.error(e.message === "Failed to fetch" ? "Keine Verbindung zum Server" : e.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="min-h-[100dvh] flex flex-col justify-center px-6 gap-4" data-testid="mobile-login">
      <img src="/app-icon-192.png" alt="" className="w-20 h-20 mx-auto" />
      <h1 className="font-['Bebas_Neue'] text-4xl text-center text-white">PFT <span className="text-[#c1ff72]">ADMIN</span></h1>
      <Input type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin-Passwort" className="h-14 text-lg bg-[#121212] border-white/20 text-white" data-testid="mobile-login-password" />
      <Button disabled={busy || !pw} className="h-14 text-lg bg-[#c1ff72] text-black hover:bg-[#a8e65c] rounded-none font-bold" data-testid="mobile-login-submit">Anmelden</Button>
    </form>
  );
}

export default function MobileApp() {
  const [token, setToken] = useState(() => localStorage.getItem("adminToken") || "");
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [offline, setOffline] = useState(false);
  const [open, setOpen] = useState(null);
  const [syncKey, setSyncKey] = useState(0);
  const sig = useRef("");

  const logout = useCallback(async (msg) => {
    const t = localStorage.getItem("adminToken");
    if (t) fetch(`${API}/admin/logout`, { method: "POST", headers: { "X-Admin-Token": t } }).catch(() => {});
    localStorage.removeItem("adminToken");
    setToken(""); setData(null);
    if (msg) toast.error(msg, { id: "session" });
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const d = await apiCall(token, "/app/admin/dashboard");
      setData(d); setOffline(false);
      const s = JSON.stringify([d.stats, d.requests.map((r) => r.appointment_id + r.status)]);
      if (sig.current && s !== sig.current) setSyncKey((k) => k + 1);
      sig.current = s;
    } catch (e) { if (e.status !== 401) setOffline(true); }
  }, [token]);

  useEffect(() => { registerSW().catch(() => {}); }, []);
  useEffect(() => {
    const expired = (ev) => logout(typeof ev?.detail === "string" && ev.detail.includes("Inaktivität") ? ev.detail : "Sitzung abgelaufen – bitte erneut anmelden");
    window.addEventListener("admin-auth-expired", expired);
    return () => window.removeEventListener("admin-auth-expired", expired);
  }, [logout]);
  useEffect(() => {
    if (!token) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    const vis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("online", load);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", vis); window.removeEventListener("online", load); };
  }, [token, load]);

  // Client-side inactivity logout (server enforces it as well)
  useEffect(() => {
    if (!token) return undefined;
    let last = Date.now();
    let limit = 30;
    apiCall(token, "/app/admin/settings").then((d) => { limit = d.idle_minutes; }).catch(() => {});
    const touch = () => { last = Date.now(); };
    const evs = ["pointerdown", "keydown", "scroll"];
    evs.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
    const id = setInterval(() => { if (Date.now() - last > limit * 60000) logout("Automatisch abgemeldet wegen Inaktivität"); }, 30000);
    return () => { evs.forEach((ev) => window.removeEventListener(ev, touch)); clearInterval(id); };
  }, [token, logout]);

  // Deep links from push notifications (?anfrage=ID / ?termin=ID) – they only open, never confirm
  const openFromUrl = useCallback(async (url) => {
    const p = new URL(url, window.location.origin).searchParams;
    const id = p.get("anfrage") || p.get("termin");
    if (!id || !token) return;
    if (p.get("anfrage")) setTab("requests");
    try { setOpen({ e: await apiCall(token, `/calendar/admin/appointments/${id}`), mode: "view" }); } catch (e) { toast.error(e.message); }
    window.history.replaceState(null, "", "/app");
  }, [token]);
  useEffect(() => { openFromUrl(window.location.href); }, [openFromUrl]);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const h = (ev) => ev.data?.type === "open-url" && openFromUrl(ev.data.url);
    navigator.serviceWorker.addEventListener("message", h);
    return () => navigator.serviceWorker.removeEventListener("message", h);
  }, [openFromUrl]);

  const changed = () => { load(); setSyncKey((k) => k + 1); };
  const sub = tab === "invoices" || tab === "rules";
  const pendingCount = data?.stats?.open_requests || 0;

  return (
    <div className="min-h-[100dvh] bg-[#0A0A0A] text-white" data-testid="mobile-app">
      <Helmet>
        <title>PFT Admin</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="manifest" href="/app-manifest.json" />
        <meta name="theme-color" content="#0A0A0A" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="PFT Admin" />
        <link rel="apple-touch-icon" href="/apple-touch-icon-app.png" />
        <style>{"[data-sonner-toaster]{bottom:calc(84px + env(safe-area-inset-bottom)) !important}"}</style>
      </Helmet>
      {!token ? <Login onLogin={(t) => { localStorage.setItem("adminToken", t); setToken(t); }} /> : (
        <>
          <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-4 flex items-center gap-3" style={{ paddingTop: "max(env(safe-area-inset-top), 10px)", paddingBottom: 10 }}>
            {sub && <button type="button" onClick={() => setTab("more")} className="p-1" data-testid="mobile-back"><ArrowLeft className="w-5 h-5" /></button>}
            <h1 className="font-['Bebas_Neue'] text-2xl tracking-wide flex-1" data-testid="mobile-title">{TITLES[tab]}</h1>
            <button type="button" onClick={changed} className="p-2 text-[#A1A1AA]" data-testid="mobile-refresh"><RefreshCw className="w-5 h-5" /></button>
          </header>
          {offline && <div className="bg-red-600/90 text-white text-sm px-4 py-2 flex items-center gap-2" data-testid="mobile-offline"><WifiOff className="w-4 h-4" />Keine Verbindung – Daten evtl. nicht aktuell. Aktionen werden nicht gespeichert.</div>}
          <main className="px-3 pt-4" style={{ paddingBottom: "calc(84px + env(safe-area-inset-bottom))" }}>
            {tab === "dashboard" && <DashboardView token={token} data={data} onOpen={(e) => setOpen({ e, mode: "view" })} onAlternative={(e) => setOpen({ e, mode: "alternative" })} onChanged={changed} go={setTab} />}
            {tab === "requests" && <RequestsView token={token} data={data} onChanged={changed} onAlternative={(e) => setOpen({ e, mode: "alternative" })} />}
            {tab === "calendar" && <KalenderTab key={syncKey} adminToken={token} compact />}
            {tab === "customers" && <KundenTab adminToken={token} />}
            {tab === "more" && <MoreView token={token} go={setTab} onLogout={() => logout()} />}
            {tab === "invoices" && <RechnungenTab adminToken={token} />}
            {tab === "rules" && <VerfuegbarkeitenTab adminToken={token} />}
          </main>
          <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#121212] border-t border-white/10 grid grid-cols-5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-nav">
            {NAV.map(([id, label, Icon]) => {
              const active = tab === id || (id === "more" && sub);
              return (
                <button key={id} type="button" onClick={() => setTab(id)} className={`relative flex flex-col items-center justify-center h-16 gap-1 text-[11px] ${active ? "text-[#c1ff72]" : "text-[#A1A1AA]"}`} data-testid={`mobile-nav-${id}`}>
                  <Icon className="w-6 h-6" />{label}
                  {id === "requests" && pendingCount > 0 && <span className="absolute top-1.5 right-[calc(50%-20px)] min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-400 text-black text-[10px] font-bold flex items-center justify-center" data-testid="mobile-nav-badge">{pendingCount}</span>}
                </button>
              );
            })}
          </nav>
          {open && <AppointmentDialog key={open.e.appointment_id + open.mode} token={token} event={open.e} initialMode={open.mode} onClose={() => setOpen(null)}
            onChanged={async () => { changed(); setOpen(null); }} />}
        </>
      )}
    </div>
  );
}
